/**
 * Route guard for the Work Routing Settings page
 * (S1-P6-WORK-QUEUE-ROUTING-SETTINGS-ROLE-ONLY-BUILD-01). Mirrors
 * RequireNavSection / RequireLeadsWorkspace: the pure decision lives in
 * decideRoutingSettingsAccess (work-routing-settings-view.ts) and keys off the
 * caller's role - owner / gm_agm / regional_head (the routing-configuration
 * roles). Any other role redirects to its own homePath. This is a role-only
 * gate: NO /me flag signal, NO feature flag.
 *
 * Backend route auth (canConfigureRouting + the row-level org-default /
 * clinic-scope policy) remains the real security boundary; this guard is UX
 * cleanliness only.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { decideRoutingSettingsAccess } from '@/dashboard/work-routing/work-routing-settings-view';
import type { DashboardMe } from '@/dashboard/types';

export interface RequireRoutingSettingsViewProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
  /** Rendered when access is granted. Defaults to `<Outlet />`. */
  readonly children?: React.ReactNode;
}

/** Pure presentational view - testable via react-dom/server. */
export function RequireRoutingSettingsView({
  loading,
  error,
  me,
  children,
}: RequireRoutingSettingsViewProps): JSX.Element | null {
  const decision = decideRoutingSettingsAccess({ loading, error, me });
  switch (decision.kind) {
    case 'wait':
      return null;
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'render':
      return <>{children ?? <Outlet />}</>;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return null;
    }
  }
}

export default function RequireRoutingSettings(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  if (!ctx) {
    return <Navigate to="/login" replace />;
  }
  return (
    <RequireRoutingSettingsView
      loading={ctx.loading}
      error={ctx.error}
      me={ctx.me}
    />
  );
}
